import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, ChevronRight, Flame, GraduationCap, Megaphone, TrendingDown, TrendingUp, Video,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { niceCeiling, readableSeconds } from './hoursScale';
import { cn } from '@/lib/utils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HOURS ACTIVITY, AND WHAT IS ON TODAY
 *
 * The trends above answer "which way is this going" over months. These two are
 * the near view: how much learning actually happened this week, and what the
 * organisation has booked for today.
 *
 * Both are measured. Hours come from `learning_days`, which records only time
 * genuinely spent — video actually watched, papers actually sat — never an
 * estimate from a course's advertised duration. The schedule is the calendar,
 * filtered to today in LOCAL time so a 23:58 session is still today.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export interface HoursBucket {
  key: string;
  label: string;
  hours: number;
  isCurrent: boolean;
}

/** One range's summary — see `HoursRange` on the API for why it is seconds. */
export interface HoursRange {
  seconds: number;
  priorSeconds: number;
  /** Null when the baseline was too small for a percentage to mean anything. */
  changePercent: number | null;
  busiest: { label: string; seconds: number } | null;
}

export interface HoursData {
  week: HoursBucket[];
  fourWeeks: HoursBucket[];
  /** One per range the control offers, so the headline matches the bars. */
  ranges: { week: HoursRange; fourWeeks: HoursRange };
}

export interface ScheduleEntry {
  id: string;
  title: string;
  type: 'ORGANIZATION' | 'DEPARTMENT' | 'LEARNING';
  startAt: string;
  endAt: string;
  allDay: boolean;
  location: string | null;
  meetingUrl: string | null;
  departmentName: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════

type Range = 'week' | 'fourWeeks';

/** What each range is called, in the two places it has to be named. */
const RANGE: Record<Range, { option: string; period: string; before: string }> = {
  week: { option: 'This week', period: 'this week', before: 'the week before' },
  fourWeeks: {
    option: 'Last 4 weeks',
    period: 'over 4 weeks',
    before: 'the 4 weeks before',
  },
};

/**
 * The comparison line, and the only part of this card that is allowed to say
 * nothing at all.
 *
 * `changePercent` is null when the previous period was too small to divide by
 * — see `MIN_BASELINE_SECONDS` on the API.
 *
 * ── Why that case now renders NOTHING ─────────────────────────────────────
 *
 * It first rendered the baseline plainly instead — "10s the week before" —
 * on the reasoning that the figure explained why no percentage was shown.
 * It does, and nobody needs the explanation. Ten seconds is not a comparison
 * a reader can do anything with; printing it just moves the meaningless
 * number from the pill to the line under it.
 *
 * So the row disappears, and the card leads with the total. A comparison
 * appears when there is one worth making.
 */
function Comparison({ summary, range }: { summary: HoursRange; range: Range }) {
  if (summary.changePercent === null) return null;

  const rising = summary.changePercent >= 0;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 pt-2">
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold',
          rising ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive',
        )}
      >
        {rising ? (
          <TrendingUp className="h-3 w-3" aria-hidden />
        ) : (
          <TrendingDown className="h-3 w-3" aria-hidden />
        )}
        {rising ? '+' : ''}
        {Math.round(summary.changePercent)}%
      </span>
      <span className="text-xs text-muted-foreground">
        {rising ? 'more' : 'less'} than {RANGE[range].before}
      </span>
    </div>
  );
}

/** One supporting figure under the headline. */
function Footnote({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" aria-hidden />
      {children}
    </span>
  );
}

export function HoursActivity({ hours }: { hours: HoursData }) {
  const [range, setRange] = useState<Range>('week');
  const buckets = range === 'week' ? hours.week : hours.fourWeeks;
  const summary = hours.ranges[range];
  const names = RANGE[range];

  /**
   * One scale across the whole chart, rounded up to a round figure.
   *
   * This was a hard floor of one hour, which protected against a four-minute
   * day drawing as a full bar by making every real week unreadable instead —
   * eighteen minutes of activity became six stubs against an axis marked `1h`.
   * `niceCeiling` keeps the protection without fixing the number.
   */
  const peak = niceCeiling(Math.max(...buckets.map((b) => b.hours * 3600), 0));

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 pb-2 pt-4">
        {/*
          "Organisation", because it is — and because the card above it on this
          same column shows the reader's OWN learning in the same shape. Two
          cards of bars, one titled "Hours activity", invited the figure to be
          read as personal; this one is everybody's measured time added up.
        */}
        <h2 className="text-[15px] font-semibold">Organisation hours activity</h2>

        {/* The range control, as a plain select — one styled dropdown here
            would be a third pattern for the same job on one page. */}
        <select
          value={range}
          onChange={(e) => setRange(e.target.value as Range)}
          aria-label="Time range"
          className="ml-auto rounded-lg border bg-background px-2.5 py-1 text-xs font-medium"
        >
          <option value="week">{RANGE.week.option}</option>
          <option value="fourWeeks">{RANGE.fourWeeks.option}</option>
        </select>
      </div>

      {/*
        ── The total first, the comparison second ────────────────────────────

        This card led with a percentage and never said the figure it was a
        percentage OF. A reader saw "+10470%" above a chart of small bars and
        had no way to find out that it meant eighteen minutes against ten
        seconds. The amount is the fact; the change is the commentary.
      */}
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4">
        <span className="font-mono text-2xl font-semibold leading-none tabular-nums">
          {readableSeconds(summary.seconds)}
        </span>
        <span className="text-xs text-muted-foreground">{names.period}</span>
      </div>

      {/* Renders nothing at all when there is no comparison worth making, so
          the card closes up rather than leaving an empty row. */}
      <Comparison summary={summary} range={range} />

      {summary.busiest && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 pb-4 pt-2">
          <Footnote icon={Flame}>
            busiest {range === 'week' ? 'day' : 'week'}{' '}
            <span className="font-medium text-foreground">{summary.busiest.label}</span>
            {', '}
            <span className="tabular-nums">{readableSeconds(summary.busiest.seconds)}</span>
          </Footnote>
        </div>
      )}

      <div className="px-4 pb-4">
        <div className="flex gap-2">
          {/* Axis marks, spaced to sit ON the gridlines. */}
          <div
            className="flex w-8 shrink-0 flex-col justify-between text-right font-mono text-[10px] text-muted-foreground/70"
            style={{ height: 150 }}
            aria-hidden
          >
            <span>{readableSeconds(peak)}</span>
            <span>{readableSeconds(peak / 2)}</span>
            <span>0</span>
          </div>

          <ul className="flex min-w-0 flex-1 items-end justify-around gap-2" style={{ height: 150 }}>
            {buckets.map((b) => (
              <li
                key={b.key}
                className="group relative flex h-full min-w-0 flex-1 flex-col justify-end"
              >
                {/*
                  The reading, on hover. A bar chart answers "which is biggest";
                  the number answers "is that a lot", and only one of those
                  fits on the axis.
                */}
                <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-foreground px-2 py-1 text-[11px] font-medium text-background group-hover:block">
                  {readableSeconds(b.hours * 3600)} · {b.label}
                </span>

                <div
                  className={cn(
                    'w-full rounded-md transition-[height] duration-500',
                    b.hours === 0
                      ? 'bg-border'
                      : b.isCurrent
                        ? 'bg-brand-600'
                        : 'bg-brand-800',
                  )}
                  /* A hairline for a day with nothing on it, so the day still
                     exists on the axis instead of vanishing from it. */
                  style={{
                    height: b.hours === 0 ? 3 : `max(6px, ${((b.hours * 3600) / peak) * 100}%)`,
                  }}
                />
              </li>
            ))}
          </ul>
        </div>

        <ul className="ml-10 flex justify-around gap-2">
          {buckets.map((b) => (
            <li
              key={b.key}
              className={cn(
                'mt-1.5 min-w-0 flex-1 truncate text-center font-mono text-[10px]',
                b.isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground',
              )}
            >
              {b.label}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════

/** How each kind of entry reads and looks. */
const KIND: Record<ScheduleEntry['type'], { icon: LucideIcon; tone: string; label: string }> = {
  LEARNING: { icon: GraduationCap, tone: 'bg-brand-50 text-brand-700 dark:bg-brand-400/15 dark:text-brand-200', label: 'Learning' },
  DEPARTMENT: { icon: Megaphone, tone: 'bg-clay-50 text-clay-700 dark:bg-clay-400/15 dark:text-clay-200', label: 'Department' },
  ORGANIZATION: { icon: Calendar, tone: 'bg-muted text-muted-foreground', label: 'Organisation' },
};

/** `10:30 – 11:30`, or "All day". */
function whenLabel(entry: ScheduleEntry): string {
  if (entry.allDay) return 'All day';
  const time = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${time(entry.startAt)} – ${time(entry.endAt)}`;
}

export function DailySchedule({ schedule }: { schedule: ScheduleEntry[] }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 px-4 pb-3 pt-4">
        <h2 className="text-[15px] font-semibold">Daily schedule</h2>
        <span className="ml-auto text-xs text-muted-foreground">
          {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })}
        </span>
      </div>

      {schedule.length === 0 ? (
        <div className="px-5 pb-8 pt-4 text-center">
          <Calendar className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" aria-hidden />
          <p className="text-sm font-medium">Nothing scheduled today</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Topics added to the calendar for today appear here.
          </p>
        </div>
      ) : (
        <ul className="divide-y border-t">
          {schedule.map((entry) => {
            const kind = KIND[entry.type];
            const Icon = kind.icon;

            return (
              <li
                key={entry.id}
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40"
              >
                {/*
                  ── TWO TARGETS, NOT ONE ──────────────────────────────────

                  The whole row used to be one `<Link>` to /calendar, with a
                  small video glyph to say a meeting link existed. Getting into
                  the meeting meant opening the calendar and finding the event
                  again — three clicks to reach a URL the row was already
                  holding.

                  The Join button cannot live inside that Link: an anchor
                  nested in an anchor is invalid, and browsers recover from it
                  by closing the outer one, so the markup would not mean what
                  it says. So the row is a plain flex container, the TEXT is
                  the link to the calendar, and Join is its own anchor beside
                  it.
                */}
                <Link
                  to="/calendar"
                  className="flex min-w-0 flex-1 items-center gap-3"
                  aria-label={`${entry.title} — open in the calendar`}
                >
                  <span
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                      kind.tone,
                    )}
                    aria-hidden
                  >
                    <Icon className="h-4 w-4" />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold">{entry.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {/* Kind, then when, then where — the three things somebody
                          checks before deciding whether this one is theirs. */}
                      {entry.departmentName ?? kind.label} · {whenLabel(entry)}
                      {entry.location && ` · ${entry.location}`}
                    </span>
                  </span>
                </Link>

                {entry.meetingUrl ? (
                  /* The way in, on the row that already knows the URL. Only
                     for an event that HAS one — a button that opens nothing is
                     worse than no button. */
                  <Button asChild size="sm" className="h-7 shrink-0 px-2.5 text-xs">
                    <a
                      href={entry.meetingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Join ${entry.title}`}
                    >
                      <Video className="h-3.5 w-3.5" />
                      Join now
                    </a>
                  </Button>
                ) : (
                  <ChevronRight
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
